import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Select, Tooltip } from 'antd';
import {
  calcTextWidth,
  SELECT_WIDTH_OFFSET_RIGHT
} from '@react-awesome-query-builder/antd/cjs/utils/domUtils';

// Custom AQB renderField widget that mirrors @react-awesome-query-builder
// FieldSelect but renders the field's `tooltip` (raw column name, set in
// formatQueryFields) as a muted subtitle below the label, matching the
// inline subtitle style used in the Group By / Aggregate Field dropdowns.
//
// FORK PIN: forked against @react-awesome-query-builder ^6.6.15 (see fe/package.json).
// It reaches into AQB internals — the deep CJS import above
// (`.../antd/cjs/utils/domUtils`) and the `mapFieldItemToOptionKeys` /
// `_value2`/`_tooltip` private option-key contract below. On any AQB version
// bump, re-verify those against AQB's own FieldSelect before trusting this.

const { Option, OptGroup } = Select;

// Pre-v3 rows and the 55 movers have no block_path; trmUtil labels them this. Kept in sync there.
const GENERAL_GROUP = 'General';

// Wide enough for a full field label plus its raw-column subtitle without truncating, and capped
// so the popup stays inside the 720px drawer it opens over.
const DROPDOWN_STYLE = { minWidth: 380, maxWidth: 560 };

// The grouped catalogue wants a tall list (fewer scroll gestures per lookup), but the list must fit
// between the control and the viewport edge. A fixed 420 did not (FIN-13440): mid-drawer at 100%
// zoom neither side had room, antd flipped the popup upward, and its top — the block-less
// functions and the first "Behavioural Intelligence" header — rendered above the viewport.
const GROUPED_LIST_MAX_HEIGHT = 420;
// antd 4's own default listHeight, so a cramped viewport never gets a list shorter than stock.
const GROUPED_LIST_MIN_HEIGHT = 256;
// Popup offset (4px) + the popup's own vertical padding + a little air from the viewport edge.
const VIEWPORT_MARGIN = 24;

/**
 * List height that fits the popup on the roomier side of the control (FIN-13440).
 *
 * antd opens below and flips upward only when below does not fit, so sizing to the larger of the
 * two spaces guarantees whichever side antd picks can hold the whole popup. No rect (not yet
 * measured, or jsdom) keeps the previous fixed height.
 */
export const fitListHeight = (rect, viewportHeight) => {
  if (!rect || !viewportHeight) return GROUPED_LIST_MAX_HEIGHT;
  const room =
    Math.max(rect.top, viewportHeight - rect.bottom) - VIEWPORT_MARGIN;
  return Math.min(
    GROUPED_LIST_MAX_HEIGHT,
    Math.max(GROUPED_LIST_MIN_HEIGHT, Math.floor(room))
  );
};

// Mirror of AQB's mapFieldItemToOptionKeys so filterOption can scan the same
// underscore-prefixed Option props AQB stamps onto each rendered option.
const mapFieldItemToOptionKeys = {
  key: '_value2',
  path: 'value',
  label: 'label',
  altLabel: 'title',
  tooltip: '_tooltip',
  grouplabel: 'grouplabel',
  fullLabel: '_fullLabel'
};

/**
 * Does this option match what the analyst typed? (FIN-12586)
 *
 * The block path is searched alongside the field's own keys. antd runs filterOption per Option
 * and never hands it the wrapping OptGroup's label, so without `_blockPath` the group header is
 * unsearchable: typing "transaction event" found nothing. That "gps location" DID work was an
 * accident of naming — those columns carry their block as a name prefix, so the full label
 * repeats the header. `transaction_event` holds legacy columns (`transaction_id` →
 * "Transaction Number") and repeats nothing, which is where the illusion broke.
 *
 * Keys are joined with NUL so a match can never straddle two fields.
 */
export const matchesFieldSearch = (input, option = {}, keysForSearch = []) => {
  const haystack = [...keysForSearch, '_blockPath']
    .map((key) => (typeof option[key] === 'string' ? option[key] : ''))
    .join('\0');

  return haystack.toLowerCase().includes(String(input ?? '').toLowerCase());
};

// The raw column and its generated label differ only in case and separators — the emitter builds
// ui_name as `column.replace("_", " ").title()`. Comparing them verbatim (FIN-12586 ticket 51)
// meant the subtitle repeated the label for 872 of 1085 fields, costing every generated v3 field
// two lines to say one thing and truncating the labels that actually needed the width.
const normaliseFieldText = (text) =>
  String(text ?? '')
    .toLowerCase()
    .replace(/[\s_]+/g, '_');

// Group headers read as a breadcrumb: "Transaction Event \u203a Card Context". The separator is a
// single glyph so the header stays one line at 12px even four levels deep.
const PATH_SEPARATOR = ' \u203a ';

// FIN-13441 \u2014 alphabetise by label. `Array.prototype.sort` with a comparator has been a stable
// sort (spec-guaranteed) since ES2019 across every engine this app ships to, so two fields
// sharing a label (the FIN-12586 collision case) keep their original relative order instead of
// shuffling between renders. [adversarial N1]
const sortByLabel = (a, b) =>
  String(a?.label ?? '').localeCompare(String(b?.label ?? ''));

/**
 * Group the flat option list by its full v3 block path (FIN-12586 ticket 51).
 *
 * DISPLAY ONLY. Every option keeps the flat storage column as its value, so the saved rule
 * identity is untouched. Nesting these in react-awesome-query-builder's own `items` config would
 * make the emitted field id the dotted path, which the engine never resolves.
 *
 * The path comes from `configFields` (the formatQueryFields output, i.e. `config.fields`): AQB's
 * buildOptions constructs each item explicitly and carries nothing off our field config.
 *
 * Returns [] when no field carries a block, so every other caller renders unchanged.
 */
export const groupItemsByPath = (items = [], configFields = {}) => {
  const byPath = new Map();

  items
    .filter((item) => !item.items)
    .forEach((item) => {
      const trail = configFields?.[item.key]?.blockTrail;
      if (!trail || !trail.length) return;
      const label = trail.join(PATH_SEPARATOR);
      if (!byPath.has(label)) byPath.set(label, { trail, items: [] });
      byPath.get(label).items.push(item);
    });

  const order = (label) => (label === GENERAL_GROUP ? 1 : 0);

  return [...byPath.entries()]
    .map(([label, { trail, items: groupItems }]) => ({
      label,
      trail,
      // FIN-13441 — the groups themselves were already sorted below, but the fields WITHIN
      // a group were returned in catalogue-arrival order, unsorted. Sort here so every call
      // site (the picker, tests, anything future) gets alphabetised fields for free.
      items: [...groupItems].sort(sortByLabel)
    }))
    .sort(
      (a, b) =>
        order(a.label) - order(b.label) || a.label.localeCompare(b.label)
    );
};

/**
 * Flat items that carry no block (FIN-13389). The Formula tab injects the variant's functions
 * into config.fields with no blockTrail; the block-grouped render path emitted only the groups and
 * nested sub-items, so every function silently dropped out of the dropdown as soon as any metadata
 * field carried a block — while rules that already referenced one still showed its label.
 */
export const ungroupedItems = (items = [], configFields = {}) =>
  // FIN-13441 — this was a bare filter, so the run of block-less options rendered in whatever
  // order the catalogue happened to arrive in rather than alphabetised.
  items
    .filter(
      (item) => !item.items && !configFields?.[item.key]?.blockTrail?.length
    )
    .sort(sortByLabel);

/**
 * The field label with the part its group header already shows removed (FIN-12586 ticket 51).
 *
 * Under "Activity Event \u203a Beneficiary Context \u203a Authentication", a label reading
 * "Beneficiary Context Authentication Biometric Used" states its own header back before saying
 * anything new. The storage column drops the event-class segment, so the label never carries the
 * full path — try the deepest suffix of the trail first and work outwards.
 *
 * RENDERED TEXT ONLY. The Option keeps its full `label` prop, so search still matches on the whole
 * name and the closed control stays unambiguous once the header is no longer on screen.
 */
export const labelWithinBlock = (label = '', trail = []) => {
  for (let start = 0; start < trail.length; start += 1) {
    const prefix = trail.slice(start).join(' ');
    if (!prefix) continue;
    const head = `${prefix} `;
    if (String(label).toLowerCase().startsWith(head.toLowerCase())) {
      const rest = String(label).slice(head.length).trim();
      // Never strip a label away entirely: a leaf named after its own block keeps its name.
      if (rest) return rest;
    }
  }
  return label;
};

const leafKeys = (fields = []) =>
  fields.flatMap((field) =>
    field.items ? leafKeys(field.items) : [field.path || field.key]
  );

/**
 * The option value the closed Select should show for the stored field id (FIN-13549/13550).
 *
 * A rule saved before FIN-13223 stores the legacy column (`activity_type`). Its config entry is a
 * `hideForSelect` alias, so no rendered Option carries that value and antd prints the raw id. Point
 * the control at the canonical twin when it is listed; otherwise the caller renders a hidden
 * Option so the label still shows. DISPLAY ONLY: the stored id is untouched until the user re-picks.
 */
export const resolveDisplayKey = (selectedKey, items, configFields) => {
  const rendered = new Set(leafKeys(items));
  if (!selectedKey || rendered.has(selectedKey)) {
    return { displayKey: selectedKey, isRendered: true };
  }
  const canonical = configFields?.[selectedKey]?.canonicalName;
  return rendered.has(canonical)
    ? { displayKey: canonical, isRendered: true }
    : { displayKey: selectedKey, isRendered: false };
};

export const renderOptionBody = ({ label, tooltip, prefix, matchesType }) => {
  const head = matchesType ? <b>{prefix + label}</b> : prefix + label;
  // The raw column identity goes on HOVER, not a second line — which is also what AQB's own
  // FieldSelect does. Inlining it (FIN-11407) cost every row a second line to repeat itself for
  // 872 of 1085 fields, and left the list visually irregular for the 213 where it differed.
  //
  // Suppressed only when the label ON SCREEN already states the column. It used to compare
  // against the untrimmed label, on the argument that the group header supplies the rest — but
  // that left `device_language` unreachable behind a row reading "Language", for the ~872
  // generated fields whose label is the column title-cased. That is most of what an analyst
  // hovers, so the hover read as broken rather than as deliberately quiet.
  const showIdentity =
    tooltip && normaliseFieldText(tooltip) !== normaliseFieldText(label);

  const body = <span className="metadata-field-option">{head}</span>;

  return showIdentity ? (
    <Tooltip
      title={<span className="metadata-field-option__raw">{tooltip}</span>}
      placement="right"
      mouseEnterDelay={0.4}
    >
      {body}
    </Tooltip>
  ) : (
    body
  );
};

const FieldSelectWithSubtitle = (props) => {
  const {
    setField,
    config,
    customProps,
    items,
    placeholder,
    selectedKey,
    selectedLabel,
    selectedAltLabel,
    selectedFullLabel,
    readonly,
    errorText
  } = props;
  const { showSearch } = customProps || {};

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [listHeight, setListHeight] = useState(GROUPED_LIST_MAX_HEIGHT);
  // The Select root, captured from the event that opens it: antd 4's Select ref exposes no DOM node.
  const selectRootRef = useRef(null);

  const measureFrom = useCallback((el) => {
    const rect = el?.getBoundingClientRect?.();
    if (rect) setListHeight(fitListHeight(rect, window.innerHeight));
  }, []);

  // FIN-13440 follow-up — measure HERE, on the event that precedes the open, not in
  // onDropdownVisibleChange. antd mounts AND positions the popup in the same commit that flips
  // `open`, so a height set from that callback only lands on the next render: the popup is already
  // aligned for the previous value and never re-aligns. `listHeight` starts at 420, so the FIRST
  // open after every mount was positioned for 420 and rendered its top above the viewport, while
  // the second and later opens reused the settled value and were correct. Switching drawer tabs
  // unmounts this pane and resets the state, which is why "leave the tab and come back" reproduced
  // it every time. Measured on keycloak-qa: first open listHeight=420 top=0, second 368 top=12.
  const captureSelectRoot = useCallback(
    (e) => {
      selectRootRef.current = e.currentTarget;
      measureFrom(e.currentTarget);
    },
    [measureFrom]
  );

  // Safety net for an open that arrives without a preceding mouse/key event (programmatic, or a
  // focus path that skips them). One render late, but better than the stale initial value.
  const onDropdownVisibleChange = useCallback(
    (visible) => {
      if (visible) measureFrom(selectRootRef.current);
      setOpen(visible);
    },
    [measureFrom]
  );

  const onChange = useCallback((key) => setField(key), [setField]);
  const onSearch = useCallback((s) => setSearchValue(s), []);

  const filterOption = useCallback(
    (input, option) => {
      const keysForSearch = (
        config.settings.fieldItemKeysForSearch || ['label']
      ).map((k) => mapFieldItemToOptionKeys[k]);

      return matchesFieldSearch(input, option, keysForSearch);
    },
    [config.settings.fieldItemKeysForSearch]
  );

  const renderItems = (fields, level = 0, trail = null) =>
    fields
      .map((field) => {
        const {
          items: sub,
          key,
          path,
          label,
          fullLabel,
          altLabel,
          tooltip,
          grouplabel,
          disabled,
          matchesType
        } = field;
        const groupPrefix = level > 0 ? '  '.repeat(level) : '';
        const prefix = level > 1 ? '  '.repeat(level - 1) : '';
        const pathKey = path || key;

        if (sub) {
          const simpleItems = sub.filter((it) => !it.items);
          const complexItems = sub.filter((it) => !!it.items);
          const out = [];
          if (simpleItems.length) {
            let gl = groupPrefix + label;
            if (tooltip) gl = <Tooltip title={tooltip}>{gl}</Tooltip>;
            out.push(
              <OptGroup key={pathKey} label={gl}>
                {renderItems(simpleItems, level + 1, trail)}
              </OptGroup>
            );
          }
          if (complexItems.length) {
            out.push(...renderItems(complexItems, level + 1, trail));
          }
          return out;
        }

        return (
          <Option
            key={pathKey}
            value={pathKey}
            title={altLabel}
            grouplabel={grouplabel}
            label={label}
            disabled={disabled}
            _fullLabel={fullLabel}
            _tooltip={tooltip}
            _blockPath={trail ? trail.join(PATH_SEPARATOR) : undefined}
            _value2={key}
          >
            {renderOptionBody({
              label: trail ? labelWithinBlock(label, trail) : label,
              tooltip,
              prefix,
              matchesType
            })}
          </Option>
        );
      })
      .flat(Infinity);

  // One column, full dropdown width, search-first. The drawer is 720px: a multi-column cascade
  // cannot fit four levels at 12px without clipping, and the analyst who already knows the field
  // name should be able to type it rather than traverse to it.
  const pathGroups = groupItemsByPath(items, config?.fields);
  const renderedItems = pathGroups.length
    ? [
        // Functions (and any other block-less field) first: they are what the analyst just
        // authored on the Functions tab and there are few of them.
        ...renderItems(ungroupedItems(items, config?.fields)),
        ...pathGroups.map(({ label, trail, items: groupItems }) => (
          <OptGroup
            key={`block:${label}`}
            label={<span className="field-block-path">{label}</span>}
          >
            {renderItems(groupItems, 1, trail)}
          </OptGroup>
        )),
        ...renderItems(items.filter((field) => field.items))
      ]
    : renderItems(items);

  const { displayKey, isRendered } = resolveDisplayKey(
    selectedKey,
    items,
    config?.fields
  );

  const selectText = selectedLabel || placeholder;
  const selectWidth = calcTextWidth(selectText);
  const isFieldSelected = !!selectedKey;
  const width =
    (isFieldSelected && !showSearch) || !selectWidth
      ? null
      : selectWidth + SELECT_WIDTH_OFFSET_RIGHT;
  const style = useMemo(() => ({ width }), [width]);

  let tooltipText = selectedAltLabel || selectedFullLabel;
  if (tooltipText === selectedLabel) tooltipText = null;

  const dropdownPlacement = config.settings.dropdownPlacement;

  const sel = (
    <Select
      open={open}
      onDropdownVisibleChange={onDropdownVisibleChange}
      onMouseDown={captureSelectRoot}
      onKeyDown={captureSelectRoot}
      popupMatchSelectWidth={false}
      // The catalogue is ~1000 fields deep; a taller list means fewer scroll gestures per lookup
      // and the group headers stay useful. Sized on open to the space around the control
      // (FIN-13440) so the popup never runs past the viewport edge.
      listHeight={pathGroups.length ? listHeight : undefined}
      dropdownStyle={pathGroups.length ? DROPDOWN_STYLE : undefined}
      style={style}
      placeholder={placeholder}
      size={config.settings.renderSize}
      onChange={onChange}
      value={displayKey || undefined}
      optionLabelProp="label"
      filterOption={filterOption}
      disabled={readonly}
      status={errorText && 'error'}
      showSearch={!!showSearch}
      searchValue={searchValue}
      onSearch={showSearch ? onSearch : undefined}
      placement={dropdownPlacement}
      {...customProps}
    >
      {renderedItems}
      {!isRendered && (
        <Option
          key={`selected:${displayKey}`}
          value={displayKey}
          label={selectedLabel || displayKey}
          style={{ display: 'none' }}
        />
      )}
    </Select>
  );

  return tooltipText ? (
    <Tooltip title={!open ? tooltipText : null}>{sel}</Tooltip>
  ) : (
    sel
  );
};

FieldSelectWithSubtitle.displayName = 'FieldSelectWithSubtitle';

export default FieldSelectWithSubtitle;
